'use client';

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';

// Environment variables with fallback values
const ENV = {
    TALLY_FORM_ID: process.env.NEXT_PUBLIC_TALLY_FORM_ID || 'w7k4Ja',
    TALLY_SCRIPT_URL: process.env.NEXT_PUBLIC_TALLY_SCRIPT_URL || 'https://tally.so/widgets/embed.js',
    TALLY_BASE_URL: process.env.NEXT_PUBLIC_TALLY_BASE_URL || 'https://tally.so/embed',
    COOKIE_CONSENT_NAME: process.env.NEXT_PUBLIC_COOKIE_CONSENT_NAME || 'cookieConsent',
    HUBSPOT_OPT_OUT_COOKIE: process.env.NEXT_PUBLIC_HUBSPOT_OPT_OUT_COOKIE || '__hs_opt_out',
    PRIMARY_COLOR: process.env.NEXT_PUBLIC_PRIMARY_COLOR || '#FF6F20',
    FORM_MIN_HEIGHT: process.env.NEXT_PUBLIC_FORM_MIN_HEIGHT || '400px',
    FORM_WIDTH_DESKTOP: process.env.NEXT_PUBLIC_FORM_WIDTH_DESKTOP || '90%',
    FORM_WIDTH_MOBILE: process.env.NEXT_PUBLIC_FORM_WIDTH_MOBILE || '94%',
    COOKIE_CACHE_DURATION: parseInt(process.env.NEXT_PUBLIC_COOKIE_CACHE_DURATION) || 500,
    CONSENT_POLLING_INTERVAL: parseInt(process.env.NEXT_PUBLIC_CONSENT_POLLING_INTERVAL) || 2000,
    TALLY_INIT_DELAY: parseInt(process.env.NEXT_PUBLIC_TALLY_INIT_DELAY) || 500,
};

// Optimized cookie parser - memoized and more efficient
const parseCookies = () => {
    if (typeof document === 'undefined') return {};

    return document.cookie
        .split(';')
        .reduce((cookies, cookie) => {
            const [name, value] = cookie.trim().split('=');
            if (name) cookies[name] = value || '';
            return cookies;
        }, {});
};

// Простіша функція завантаження Tally форми
const loadTallyForm = () => {
    // Перевіряємо чи скрипт вже є, але не блокуємо завантаження
    const existingScript = document.querySelector(`script[src*="tally.so"]`);

    if (!existingScript) {
        const script = document.createElement('script');
        script.src = ENV.TALLY_SCRIPT_URL;
        script.async = true;
        document.head.appendChild(script);
    }

    // Завжди намагаємося ініціалізувати embed через невеличку затримку
    setTimeout(() => {
        if (window.Tally && window.Tally.loadEmbeds) {
            window.Tally.loadEmbeds();
        }
    }, ENV.TALLY_INIT_DELAY);
};

const TallyForm = () => {
    const [hasConsent, setHasConsent] = useState(false);
    const [consentDeclined, setConsentDeclined] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [formLoaded, setFormLoaded] = useState(false);

    const intervalRef = useRef(null);
    const mountedRef = useRef(true);

    // Simplified consent checker - removed caching that could cause loops
    const checkConsent = useCallback(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return false;
        }

        const cookies = parseCookies();
        return cookies[ENV.COOKIE_CONSENT_NAME] === 'true' || window.cookieConsentGranted;
    }, []);

    // Simplified consent declined checker
    const checkConsentDeclined = useCallback(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return false;
        }

        const cookies = parseCookies();
        return cookies[ENV.COOKIE_CONSENT_NAME] === 'false';
    }, []);

    // Optimized cookie banner reopener
    const reopenCookieBanner = useCallback(() => {
        if (typeof document === 'undefined') return;

        // Use more efficient cookie removal
        const cookiesToRemove = [ENV.COOKIE_CONSENT_NAME, ENV.HUBSPOT_OPT_OUT_COOKIE];
        const expireDate = 'Thu, 01 Jan 1970 00:00:00 GMT';
        const cookieAttributes = 'path=/; SameSite=Lax; Secure';

        cookiesToRemove.forEach(cookieName => {
            document.cookie = `${cookieName}=; expires=${expireDate}; ${cookieAttributes}`;
        });

        // Use replace instead of reload to avoid losing form state if needed
        window.location.reload();
    }, []);

    // Cleanup effect - only runs once on mount/unmount
    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    // Initial consent check - only run once on mount
    useEffect(() => {
        const consent = checkConsent();
        const declined = checkConsentDeclined();

        console.log('Tally Form - Initial consent status:', { consent, declined });

        setHasConsent(consent);
        setConsentDeclined(declined);
        setIsLoading(false);
    }, [checkConsent, checkConsentDeclined]); // Include dependencies

    // Simplified consent polling - only start if no consent decision has been made
    useEffect(() => {
        // Don't poll if we already have a decision or if we're still loading
        if (hasConsent || consentDeclined || isLoading) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        const checkConsentPeriodically = () => {
            if (!mountedRef.current) return;

            const currentConsent = checkConsent();
            const currentDeclined = checkConsentDeclined();

            if (currentConsent && !hasConsent) {
                setHasConsent(true);
            }
            if (currentDeclined && !consentDeclined) {
                setConsentDeclined(true);
            }
        };

        // Only start polling if we don't have an interval running
        if (!intervalRef.current) {
            intervalRef.current = setInterval(checkConsentPeriodically, ENV.CONSENT_POLLING_INTERVAL);
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [hasConsent, consentDeclined, isLoading, checkConsent, checkConsentDeclined]); // Include all dependencies

    // Form loading effect - load Tally when consent is given
    useEffect(() => {
        if (!hasConsent || formLoaded) {
            return;
        }

        console.log('Initializing Tally form...');

        const initTallyForm = () => {
            try {
                loadTallyForm();
                setFormLoaded(true);
                console.log('Tally form initialization completed');
            } catch (error) {
                console.error('Error loading Tally form:', error);
            }
        };

        // Listen for consent events
        const handleConsent = (event) => {
            if (event.detail?.consent && !formLoaded) {
                initTallyForm();
            }
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('cookieConsentGranted', handleConsent);
        }

        // Load form immediately if consent is already given
        initTallyForm();

        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('cookieConsentGranted', handleConsent);
            }
        };
    }, [hasConsent, formLoaded]);

    // Generate Tally embed URL - moved outside of useMemo to avoid recalculation
    const tallyEmbedUrl = `${ENV.TALLY_BASE_URL}/${ENV.TALLY_FORM_ID}?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1`;

    // Simplified loading component - no useMemo needed for simple JSX
    const LoadingComponent = (
        <div className="tally-form-container">
            <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Завантаження...</p>
            </div>
            <style jsx>{`
                .tally-form-container {
                    min-height: ${ENV.FORM_MIN_HEIGHT};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border-radius: 12px;
                    border: 2px dashed #dee2e6;
                }
                .loading-spinner {
                    text-align: center;
                    color: #6c757d;
                }
                .spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #f3f3f3;
                    border-top: 4px solid ${ENV.PRIMARY_COLOR};
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 16px;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );

    if (isLoading) {
        return LoadingComponent;
    }

    // Show consent declined message
    if (consentDeclined && !hasConsent) {
        return (
            <div className="tally-form-container">
                <div className="consent-message">
                    <div className="consent-icon">🍪</div>
                    <h3>Форма недоступна</h3>
                    <p>
                        Для використання форми зворотнього зв&#39;язку необхідно дати згоду на використання cookies.
                        Ви раніше відхилили їх використання.
                    </p>
                    <button
                        className="consent-button"
                        onClick={reopenCookieBanner}
                    >
                        Переглянути налаштування cookies
                    </button>
                </div>
                <style jsx>{`
                    .tally-form-container {
                        min-height: 500px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
                        border-radius: 16px;
                        border: 2px solid #ffeaa7;
                        padding: 40px 20px;
                        text-align: center;
                    }
                    .consent-message {
                        max-width: 400px;
                    }
                    .consent-icon {
                        font-size: 48px;
                        margin-bottom: 20px;
                    }
                    .consent-message h3 {
                        color: #856404;
                        margin-bottom: 16px;
                        font-size: 24px;
                        font-weight: 600;
                    }
                    .consent-message p {
                        color: #856404;
                        line-height: 1.6;
                        font-size: 16px;
                        margin-bottom: 24px;
                    }
                    .consent-button {
                        background: ${ENV.PRIMARY_COLOR};
                        height: 48px;
                        color: white;
                        border: none;
                        border-radius: 12px;
                        padding: 14px 28px;
                        font-size: 16px;
                        font-weight: 600;
                        cursor: pointer;
                        transition: all 0.3s ease;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                    }
                    .consent-button:hover {
                        transform: scale(1.05);
                        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.075);
                    }
                    .consent-button:active {
                        transform: translateY(0);
                    }
                `}</style>
            </div>
        );
    }

    // Show waiting for consent message
    if (!hasConsent && !consentDeclined) {
        return (
            <div className="tally-form-container">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                    <p>Для завантаження форми зворотнього зв&#39;язку необхідно дати згоду на cookie</p>
                </div>
                <style jsx>{`
                    .tally-form-container {
                        min-height: 100%;
                        margin: 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #f8f9fa;
                        border-radius: 12px;
                        border: 2px dashed #dee2e6;
                    }
                    .loading-spinner {
                        text-align: center;
                        color: #6c757d;
                        padding: 16px;
                    }
                    .spinner {
                        width: 40px;
                        height: 40px;
                        border: 4px solid #f3f3f3;
                        border-top: 4px solid ${ENV.PRIMARY_COLOR};
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                        margin: 0 auto 16px;
                    }
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Show the Tally form when consent is given
    return (
        <div className="tally-form-container">
            <iframe
                data-tally-src={tallyEmbedUrl}
                loading="lazy"
                width="100%"
                height="auto"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="SkyLang"
            />
            <style jsx>{`
                .tally-form-container {
                    border-radius: 12px;
                    overflow: hidden;
                    min-height: ${ENV.FORM_MIN_HEIGHT};
                    width: ${ENV.FORM_WIDTH_DESKTOP};
                    padding: 0 3%; /* Adds 3% padding on each side for mobile */
                    box-sizing: border-box;
                }

                .tally-form-container iframe {
                    border-radius: 12px;
                    width: 100%;
                }

                /* Mobile responsive styles */
                @media (max-width: 768px) {
                    .tally-form-container {
                        width: ${ENV.FORM_WIDTH_MOBILE};
                        margin: 0 auto;
                        padding: 0;
                    }
                }
            `}</style>
        </div>
    );
};

export default TallyForm;